import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { Brain, Users, Plus, Pencil, Trash2, CheckCircle, Clock, Eye, MessageSquare, FileText, Target, Lightbulb, BookOpen, Send } from 'lucide-react';
import SearchableAthleteSelect from '../components/SearchableAthleteSelect';
import TargetingField from '../components/TargetingField';
import AttachmentsField, { AttachmentsView } from '../components/AttachmentsField';
import AthleteSelectorHeader from '../components/AthleteSelectorHeader';
import MentorshipPanel from '../components/MentorshipPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENTRY_TYPE_CONFIG = {
  mental_task: { label: 'Mental Task', icon: Target, color: 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200 dark:border-blue-800' },
  reflection: { label: 'Reflection', icon: Lightbulb, color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-200 dark:border-amber-800' },
  note: { label: 'Note', icon: FileText, color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-900/20', border: 'border-emerald-200 dark:border-emerald-800' },
  assignment: { label: 'Assignment', icon: BookOpen, color: 'text-purple-600', bg: 'bg-purple-50 dark:bg-purple-900/20', border: 'border-purple-200 dark:border-purple-800' },
};

const INSIGHT_TYPE_CONFIG = {
  development_update: { label: 'Development Update', color: 'text-blue-600', bg: 'bg-blue-50' },
  behavioral_note: { label: 'Behavioral Note', color: 'text-amber-600', bg: 'bg-amber-50' },
  progress_summary: { label: 'Progress Summary', color: 'text-emerald-600', bg: 'bg-emerald-50' },
  educational_insight: { label: 'Educational Insight', color: 'text-purple-600', bg: 'bg-purple-50' },
};

const STATUS_CONFIG = {
  pending: { label: 'Pending', icon: Clock, color: 'text-amber-600 bg-amber-50 border-amber-200' },
  completed: { label: 'Completed', icon: CheckCircle, color: 'text-blue-600 bg-blue-50 border-blue-200' },
  reviewed: { label: 'Reviewed', icon: Eye, color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
};

const MentalPerformanceHub = () => {
  const { user, hasModuleAccess } = useAuth();
  const isStaffOrAdmin = user?.role === 'Admin' || user?.role === 'Staff';
  const isPlayer = user?.role === 'Player';
  const isParent = user?.role === 'Parent';

  // Phase 84.2 — Subsection permission gates. Admin always sees every tab.
  // Player and Parent roles retain the default flow (Athlete Space + Parent
  // Insights respectively) — the subsection perms only tighten access for
  // staff assigned via Access Control.
  const canViewAthleteSpace = user?.role === 'Admin'
    || isPlayer
    || (hasModuleAccess?.('mental_hub.athlete_space', 'view') ?? true);
  const canViewParentInsights = user?.role === 'Admin'
    || isParent
    || (hasModuleAccess?.('mental_hub.parent_insights', 'view') ?? true);
  const canViewMentorship = user?.role === 'Admin'
    || (isStaffOrAdmin && (hasModuleAccess?.('mental_hub.mentorship', 'view') ?? true));

  const [athletes, setAthletes] = useState([]);
  const [tagMap, setTagMap] = useState({});  // resolves target_tag_ids → athletes
  const [entries, setEntries] = useState([]);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  // Default tab honours subsection perms so a staff user with only Mentorship
  // view lands directly on the Mentorship tab instead of a blocked default.
  const defaultTab = isParent && canViewParentInsights ? 'parent-space'
    : canViewAthleteSpace ? 'athlete-space'
    : canViewParentInsights ? 'parent-space'
    : canViewMentorship ? 'mentorship'
    : 'athlete-space';
  const [activeTab, setActiveTab] = useState(defaultTab);

  // Dialogs
  const [createEntryOpen, setCreateEntryOpen] = useState(false);
  const [createInsightOpen, setCreateInsightOpen] = useState(false);
  const [viewEntryOpen, setViewEntryOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [entryResponses, setEntryResponses] = useState([]);
  const [newResponse, setNewResponse] = useState('');

  // Filters
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAthlete, setFilterAthlete] = useState('');
  // Phase 81 — accept ?athlete=<id> deep-link from Athlete 360 cards.
  const [searchParams] = useSearchParams();

  // Forms
  const emptyEntry = {
    title: '', description: '', entry_type: 'mental_task',
    due_date: '',
    attachments: [],
    targeting: { mode: 'all', groups: [], athlete_ids: [], parent_scope: false },
  };
  const emptyInsight = {
    title: '', content: '', insight_type: 'development_update',
    allow_parent_reply: true,
    attachments: [],
    targeting: { mode: 'all', groups: [], athlete_ids: [], parent_scope: true },
  };
  const [entryForm, setEntryForm] = useState(emptyEntry);
  const [insightForm, setInsightForm] = useState(emptyInsight);

  useEffect(() => {
    axios.get(`${API}/athletes`).then(r => setAthletes(r.data)).catch(() => {});
    axios.get(`${API}/tags/map`).then(r => setTagMap(r.data || {})).catch(() => {});
    fetchEntries();
    fetchInsights();
  }, []);

  // Phase 81 — pre-select athlete from ?athlete=<id> deep-link.
  // Set unconditionally; visibility filter may strip contract fixtures
  // from the athletes list but the URL intent is honoured regardless.
  useEffect(() => {
    const aid = searchParams.get('athlete') || searchParams.get('athlete_id');
    if (aid) setFilterAthlete(aid);
  }, [searchParams]);

  const fetchEntries = async () => {
    try {
      const r = await axios.get(`${API}/mental-hub/entries`);
      setEntries(r.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const fetchInsights = async () => {
    try {
      const r = await axios.get(`${API}/mental-hub/parent-insights`);
      setInsights(r.data);
    } catch { /* ignore */ }
  };

  const handleCreateEntry = async () => {
    if (!entryForm.title) { toast.error('Title is required'); return; }
    const t = entryForm.targeting || {};
    if (t.mode === 'group' && (!t.groups || t.groups.length === 0)) {
      toast.error('Select at least one group'); return;
    }
    if (t.mode === 'individual' && (!t.athlete_ids || t.athlete_ids.length === 0)) {
      toast.error('Select at least one athlete'); return;
    }
    try {
      await axios.post(`${API}/mental-hub/entries`, entryForm);
      toast.success('Entry created');
      setCreateEntryOpen(false);
      setEntryForm(emptyEntry);
      fetchEntries();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Failed to create');
    }
  };

  const handleCreateInsight = async () => {
    if (!insightForm.title || !insightForm.content) {
      toast.error('Title and content are required'); return;
    }
    const t = insightForm.targeting || {};
    if (t.mode === 'group' && (!t.groups || t.groups.length === 0)) {
      toast.error('Select at least one group'); return;
    }
    if (t.mode === 'individual' && (!t.athlete_ids || t.athlete_ids.length === 0)) {
      toast.error('Select at least one athlete'); return;
    }
    try {
      await axios.post(`${API}/mental-hub/parent-insights`, insightForm);
      toast.success('Parent insight created');
      setCreateInsightOpen(false);
      setInsightForm(emptyInsight);
      fetchInsights();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Failed to create');
    }
  };

  const openEntryDetail = async (entry) => {
    setSelectedEntry(entry);
    try {
      const r = await axios.get(`${API}/mental-hub/entries/${entry.id}`);
      setEntryResponses(r.data.responses || []);
    } catch { setEntryResponses([]); }
    setViewEntryOpen(true);
  };

  const handleStatusChange = async (entryId, newStatus) => {
    try {
      await axios.put(`${API}/mental-hub/entries/${entryId}/status?status=${newStatus}`);
      toast.success(`Status updated to ${newStatus}`);
      fetchEntries();
      if (selectedEntry?.id === entryId) setSelectedEntry(prev => ({ ...prev, status: newStatus }));
    } catch { toast.error('Failed to update status'); }
  };

  const handleAddResponse = async () => {
    if (!newResponse.trim() || !selectedEntry) return;
    try {
      await axios.post(`${API}/mental-hub/responses`, { entry_id: selectedEntry.id, message: newResponse });
      toast.success('Response added');
      setNewResponse('');
      const r = await axios.get(`${API}/mental-hub/entries/${selectedEntry.id}`);
      setEntryResponses(r.data.responses || []);
    } catch { toast.error('Failed to add response'); }
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm('Delete this entry and all its responses?')) return;
    try {
      await axios.delete(`${API}/mental-hub/entries/${id}`);
      toast.success('Deleted');
      fetchEntries();
    } catch { toast.error('Failed to delete'); }
  };

  const handleDeleteInsight = async (id) => {
    if (!window.confirm('Delete this parent insight?')) return;
    try {
      await axios.delete(`${API}/mental-hub/parent-insights/${id}`);
      toast.success('Deleted');
      fetchInsights();
    } catch { toast.error('Failed to delete'); }
  };

  // Filtered entries
  const filteredEntries = useMemo(() => {
    let result = entries;
    if (filterType !== 'all') result = result.filter(e => e.entry_type === filterType);
    if (filterStatus !== 'all') result = result.filter(e => e.status === filterStatus);
    if (filterAthlete) {
      const athlete = athletes.find(a => a.id === filterAthlete);
      const squad = athlete?.squad;
      const athleteTagIds = new Set(tagMap[filterAthlete] || []);
      result = result.filter(e => {
        if (e.target_type === 'all') return true;
        if ((e.target_athletes || []).includes(filterAthlete)) return true;
        if (squad && (e.target_squads || []).includes(squad)) return true;
        // Resolve via tags: entry matches if any of its target_tag_ids belongs to this athlete
        if ((e.target_tag_ids || []).some(t => athleteTagIds.has(t))) return true;
        return false;
      });
    }
    return result;
  }, [entries, filterType, filterStatus, filterAthlete, athletes, tagMap]);

  const getAthleteNames = (entry) => {
    const t = entry.targeting;
    if (t && typeof t === 'object') {
      if (t.mode === 'all') return t.parent_scope ? 'All Parents' : 'All Athletes';
      if (t.mode === 'group') {
        const gs = t.groups || [];
        return gs.length > 0 ? gs.join(', ') : 'No groups';
      }
      if (t.mode === 'individual') {
        const ids = t.athlete_ids || [];
        const names = ids.slice(0, 3).map(id => athletes.find(a => a.id === id)?.full_name || id);
        if (ids.length > 3) names.push(`+${ids.length - 3} more`);
        return names.join(', ') || 'No athletes';
      }
    }
    if (entry.target_type === 'all') return 'All Athletes';
    const names = (entry.target_athletes || []).map(id => athletes.find(a => a.id === id)?.full_name || id).slice(0, 3);
    const squads = entry.target_squads || [];
    const parts = [...squads, ...names];
    const total = squads.length + (entry.target_athletes || []).length;
    if (total > 3) parts.push(`+${total - 3} more`);
    return parts.join(', ') || 'No target';
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="mental-hub-page">
        <div className="flex justify-between items-center mb-5">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Mental Performance Hub
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mt-0.5 text-sm">Structured athlete development & parent communication</p>
          </div>
        </div>

        {/* Phase 6 — staff/admin athlete-first workflow. The selector drives
            filterAthlete which already powers `filteredEntries` for the
            Athlete Space tab. */}
        {isStaffOrAdmin && (
          <div className="mb-4">
            <AthleteSelectorHeader
              athletes={athletes}
              value={filterAthlete}
              onChange={setFilterAthlete}
              label="Mental Hub · Athlete-Specific View"
              subtitle="Pick an athlete to see only their entries, reflections, and assignments. Academy-wide resources do not appear here."
              testIdPrefix="mental-hub-athlete"
              emptyState={<div className="hidden" />}
            />
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-5">
            {!isParent && canViewAthleteSpace && <TabsTrigger value="athlete-space" className="gap-1.5" data-testid="tab-athlete-space"><Brain className="w-3.5 h-3.5" /> Athlete Space</TabsTrigger>}
            {!isPlayer && canViewParentInsights && <TabsTrigger value="parent-space" className="gap-1.5" data-testid="tab-parent-space"><Users className="w-3.5 h-3.5" /> Parent Insights</TabsTrigger>}
            {canViewMentorship && <TabsTrigger value="mentorship" className="gap-1.5" data-testid="tab-mentorship"><Users className="w-3.5 h-3.5" /> Mentorship</TabsTrigger>}
          </TabsList>

          {/* Phase 6 — Mentorship Programme tab (Admin + Mental Coach + opted-in staff). */}
          {isStaffOrAdmin && (
            <TabsContent value="mentorship" data-testid="mentorship-tabcontent">
              <MentorshipPanel athletes={athletes} focusAthleteId={filterAthlete || null} />
            </TabsContent>
          )}

          {/* ======== ATHLETE MENTAL SPACE ======== */}
          <TabsContent value="athlete-space">
            <div className="flex gap-3 items-end flex-wrap mb-4">
              <div className="flex gap-1.5 flex-wrap">
                {[{ v: 'all', l: 'All Types' }, ...Object.entries(ENTRY_TYPE_CONFIG).map(([k, v]) => ({ v: k, l: v.label }))].map(t => (
                  <button key={t.v} onClick={() => setFilterType(t.v)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filterType === t.v ? 'bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-900' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200'}`} data-testid={`filter-type-${t.v}`}>{t.l}</button>
                ))}
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="reviewed">Reviewed</SelectItem>
                </SelectContent>
              </Select>
              <div className="ml-auto">
                {isStaffOrAdmin && (
                  <Button onClick={() => { setEntryForm(emptyEntry); setCreateEntryOpen(true); }} className="bg-pfap-green hover:bg-pfap-green/90 h-9" data-testid="create-entry-btn">
                    <Plus className="w-4 h-4 mr-1" /> New Entry
                  </Button>
                )}
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" /></div>
            ) : filteredEntries.length === 0 ? (
              <Card><CardContent className="py-12 text-center">
                <Brain className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                {isPlayer ? (
                  <>
                    <p className="text-gray-600 dark:text-gray-300 text-sm font-medium">No mental tasks assigned yet</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Your coach will assign reflections, tasks, and mental performance exercises here. You can also add your own reflection.</p>
                  </>
                ) : isStaffOrAdmin ? (
                  <p className="text-gray-500 text-sm">No entries found. Create your first mental performance entry.</p>
                ) : (
                  <p className="text-gray-500 text-sm">No entries to display.</p>
                )}
              </CardContent></Card>
            ) : (
              <div className="space-y-3" data-testid="entries-list">
                {filteredEntries.map(entry => {
                  const cfg = ENTRY_TYPE_CONFIG[entry.entry_type] || ENTRY_TYPE_CONFIG.note;
                  const stCfg = STATUS_CONFIG[entry.status] || STATUS_CONFIG.pending;
                  const Icon = cfg.icon;
                  const StIcon = stCfg.icon;
                  return (
                    <Card key={entry.id} className={`border ${cfg.border} hover:shadow-sm transition-shadow cursor-pointer`} onClick={() => openEntryDetail(entry)} data-testid={`entry-card-${entry.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.bg}`}>
                            <Icon className={`w-4 h-4 ${cfg.color}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{entry.title}</p>
                              <Badge className={`text-[10px] px-1.5 py-0 border ${stCfg.color}`}>
                                <StIcon className="w-3 h-3 mr-0.5" />{stCfg.label}
                              </Badge>
                              <Badge className={`text-[10px] px-1.5 py-0 ${cfg.bg} ${cfg.color} border ${cfg.border}`}>{cfg.label}</Badge>
                            </div>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                              {getAthleteNames(entry)}
                              {entry.due_date ? ` · Due: ${entry.due_date}` : ''}
                              {entry.response_count > 0 ? ` · ${entry.response_count} response${entry.response_count > 1 ? 's' : ''}` : ''}
                            </p>
                          </div>
                          <div className="flex gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                            {isStaffOrAdmin && (
                              <>
                                <Select value={entry.status} onValueChange={v => handleStatusChange(entry.id, v)}>
                                  <SelectTrigger className="h-7 w-24 text-[10px]"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="pending">Pending</SelectItem>
                                    <SelectItem value="completed">Completed</SelectItem>
                                    <SelectItem value="reviewed">Reviewed</SelectItem>
                                  </SelectContent>
                                </Select>
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDeleteEntry(entry.id)} data-testid={`delete-entry-${entry.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                              </>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* ======== PARENT INSIGHT SPACE ======== */}
          <TabsContent value="parent-space">
            <div className="flex justify-between items-center mb-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">Controlled updates for parents — athletes cannot see this section</p>
              {isStaffOrAdmin && (
                <Button onClick={() => { setInsightForm(emptyInsight); setCreateInsightOpen(true); }} className="bg-pfap-green hover:bg-pfap-green/90 h-9" data-testid="create-insight-btn">
                  <Plus className="w-4 h-4 mr-1" /> New Insight
                </Button>
              )}
            </div>

            {insights.length === 0 ? (
              <Card><CardContent className="py-12 text-center">
                <Users className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                {isParent ? (
                  <>
                    <p className="text-gray-600 dark:text-gray-300 text-sm font-medium">No parent insights yet</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Coaches share behavioural, developmental, and progress notes here. New insights will appear automatically.</p>
                  </>
                ) : (
                  <p className="text-gray-500 text-sm">No parent insights yet. Create one to share updates with parents.</p>
                )}
              </CardContent></Card>
            ) : (
              <div className="space-y-3" data-testid="insights-list">
                {insights.map(ins => {
                  const cfg = INSIGHT_TYPE_CONFIG[ins.insight_type] || INSIGHT_TYPE_CONFIG.development_update;
                  const targetingLabel = (() => {
                    const t = ins.targeting;
                    if (t && typeof t === 'object') {
                      if (t.mode === 'all') return 'All Parents';
                      if (t.mode === 'group') return (t.groups || []).join(', ') || 'Groups';
                      if (t.mode === 'individual') {
                        const n = (t.athlete_ids || []).length;
                        return `${n} athlete${n === 1 ? '' : 's'}`;
                      }
                    }
                    return null;
                  })();
                  return (
                    <Card key={ins.id} className="hover:shadow-sm transition-shadow" data-testid={`insight-card-${ins.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{ins.title}</p>
                              <Badge className={`text-[10px] px-1.5 py-0 ${cfg.bg} ${cfg.color}`}>{cfg.label}</Badge>
                              {targetingLabel && <Badge variant="outline" className="text-[10px] px-1.5 py-0">{targetingLabel}</Badge>}
                              {!targetingLabel && ins.athlete_name && <Badge variant="outline" className="text-[10px] px-1.5 py-0">{ins.athlete_name}{ins.athlete_squad ? ` · ${ins.athlete_squad}` : ''}</Badge>}
                              {ins.parent_acknowledged && <Badge className="text-[10px] px-1.5 py-0 bg-emerald-50 text-emerald-600 border-emerald-200">Acknowledged</Badge>}
                            </div>
                            <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">{ins.content}</p>
                            {(ins.attachments || []).length > 0 && (
                              <p className="text-[10px] text-emerald-600 mt-1">📎 {ins.attachments.length} attachment{ins.attachments.length > 1 ? 's' : ''}</p>
                            )}
                            <p className="text-[10px] text-gray-400 mt-1">By {ins.created_by_name} · {new Date(ins.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</p>
                            {ins.parent_reply && (
                              <div className="mt-2 pl-3 border-l-2 border-emerald-300">
                                <p className="text-xs text-gray-600 dark:text-gray-400"><span className="font-medium">Parent reply:</span> {ins.parent_reply}</p>
                              </div>
                            )}
                          </div>
                          {isStaffOrAdmin && <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 flex-shrink-0" onClick={() => handleDeleteInsight(ins.id)} data-testid={`delete-insight-${ins.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* ===== CREATE ENTRY DIALOG ===== */}
        <Dialog open={createEntryOpen} onOpenChange={setCreateEntryOpen}>
          <DialogContent className="max-w-lg" data-testid="create-entry-dialog">
            <DialogHeader>
              <DialogTitle>New Mental Performance Entry</DialogTitle>
              <DialogDescription>Assign tasks, reflections, or notes to athletes</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto pr-1">
              <div>
                <Label>Title *</Label>
                <Input value={entryForm.title} onChange={e => setEntryForm({ ...entryForm, title: e.target.value })} placeholder="e.g., Pre-match Visualization" data-testid="entry-title" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select value={entryForm.entry_type} onValueChange={v => setEntryForm({ ...entryForm, entry_type: v })}>
                    <SelectTrigger data-testid="entry-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(ENTRY_TYPE_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Due Date</Label>
                  <Input type="date" value={entryForm.due_date} onChange={e => setEntryForm({ ...entryForm, due_date: e.target.value })} data-testid="entry-due-date" />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={entryForm.description} onChange={e => setEntryForm({ ...entryForm, description: e.target.value })} placeholder="Instructions or details..." rows={3} data-testid="entry-description" />
              </div>
              <TargetingField
                athletes={athletes}
                value={entryForm.targeting}
                onChange={val => setEntryForm({ ...entryForm, targeting: val })}
                label="Target"
                showParentToggle={true}
                data-testid="entry-targeting"
              />
              <AttachmentsField
                value={entryForm.attachments}
                onChange={val => setEntryForm({ ...entryForm, attachments: val })}
                label="Resources"
                data-testid="entry-attachments"
              />
            </div>
            <DialogFooter>
              <Button onClick={handleCreateEntry} className="bg-pfap-green hover:bg-pfap-green/90" data-testid="submit-entry">Create Entry</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== CREATE INSIGHT DIALOG ===== */}
        <Dialog open={createInsightOpen} onOpenChange={setCreateInsightOpen}>
          <DialogContent className="max-w-lg" data-testid="create-insight-dialog">
            <DialogHeader>
              <DialogTitle>New Parent Insight</DialogTitle>
              <DialogDescription>Share a development update with parents (athletes cannot see this)</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2 max-h-[65vh] overflow-y-auto pr-1">
              <div>
                <Label>Title *</Label>
                <Input value={insightForm.title} onChange={e => setInsightForm({ ...insightForm, title: e.target.value })} placeholder="e.g., Weekly Development Summary" data-testid="insight-title" />
              </div>
              <div>
                <Label>Type</Label>
                <Select value={insightForm.insight_type} onValueChange={v => setInsightForm({ ...insightForm, insight_type: v })}>
                  <SelectTrigger data-testid="insight-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(INSIGHT_TYPE_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Content *</Label>
                <Textarea value={insightForm.content} onChange={e => setInsightForm({ ...insightForm, content: e.target.value })} placeholder="Development update for parents..." rows={4} data-testid="insight-content" />
              </div>
              <TargetingField
                athletes={athletes}
                value={insightForm.targeting}
                onChange={val => setInsightForm({ ...insightForm, targeting: { ...val, parent_scope: true } })}
                label="Send To (Parents of…)"
                showParentToggle={false}
                parentLocked={true}
                data-testid="insight-targeting"
              />
              <AttachmentsField
                value={insightForm.attachments}
                onChange={val => setInsightForm({ ...insightForm, attachments: val })}
                label="Resources"
                data-testid="insight-attachments"
              />
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={insightForm.allow_parent_reply} onChange={e => setInsightForm({ ...insightForm, allow_parent_reply: e.target.checked })} className="rounded border-gray-300" />
                <span className="text-sm text-gray-600 dark:text-gray-400">Allow parent to reply</span>
              </label>
            </div>
            <DialogFooter>
              <Button onClick={handleCreateInsight} className="bg-pfap-green hover:bg-pfap-green/90" data-testid="submit-insight">Share with Parents</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== ENTRY DETAIL DIALOG ===== */}
        <Dialog open={viewEntryOpen} onOpenChange={setViewEntryOpen}>
          <DialogContent className="max-w-2xl" data-testid="entry-detail-dialog">
            {selectedEntry && (() => {
              const cfg = ENTRY_TYPE_CONFIG[selectedEntry.entry_type] || ENTRY_TYPE_CONFIG.note;
              const stCfg = STATUS_CONFIG[selectedEntry.status] || STATUS_CONFIG.pending;
              const Icon = cfg.icon;
              return (
                <>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-md flex items-center justify-center ${cfg.bg}`}><Icon className={`w-3.5 h-3.5 ${cfg.color}`} /></div>
                      {selectedEntry.title}
                    </DialogTitle>
                    <DialogDescription asChild>
                      <div className="flex items-center gap-2">
                      <Badge className={`text-[10px] px-1.5 py-0 border ${stCfg.color}`}>{stCfg.label}</Badge>
                      <Badge className={`text-[10px] px-1.5 py-0 ${cfg.bg} ${cfg.color}`}>{cfg.label}</Badge>
                      <span className="text-xs">{getAthleteNames(selectedEntry)}</span>
                      </div>
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 max-h-[50vh] overflow-y-auto">
                    {selectedEntry.description && (
                      <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedEntry.description}</p>
                      </div>
                    )}
                    {/* Attachments */}
                    {(selectedEntry.attachments || []).length > 0 && (
                      <AttachmentsView attachments={selectedEntry.attachments} data-testid="entry-detail-attachments" />
                    )}
                    {/* Responses */}
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-2">Responses ({entryResponses.length})</p>
                      {entryResponses.length === 0 ? (
                        <p className="text-xs text-gray-400 text-center py-4">No responses yet</p>
                      ) : (
                        <div className="space-y-2">
                          {entryResponses.map(r => (
                            <div key={r.id} className="p-3 border rounded-lg bg-white dark:bg-gray-800" data-testid={`response-${r.id}`}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{r.athlete_name || 'Unknown'}</span>
                                <span className="text-[10px] text-gray-400">{new Date(r.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                              </div>
                              <p className="text-sm text-gray-600 dark:text-gray-400">{r.message}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Add response */}
                    <div className="flex gap-2">
                      <Input value={newResponse} onChange={e => setNewResponse(e.target.value)} placeholder="Add a response..." className="flex-1" data-testid="new-response-input" onKeyDown={e => { if (e.key === 'Enter') handleAddResponse(); }} />
                      <Button size="sm" onClick={handleAddResponse} className="bg-pfap-green hover:bg-pfap-green/90" data-testid="send-response"><Send className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </>
              );
            })()}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default MentalPerformanceHub;
